import type { FundamentalCatalogItem } from '@/api/types';
import { getAttitudeName } from '@/hooks/useFundamentals';
import type { AttitudeAvg, FundAvg } from '../hooks/useSafeScores';

/** Un eje del radar: el fundamental completo, o uno de sus comportamientos. */
export interface RadarAxis {
  id: string;
  kind: 'fundamental' | 'attitude';
  /** Nombre ya localizado; solo se usa en el tooltip al pasar el cursor. */
  name: string;
  /** Clase Font Awesome o URL de imagen; ya resuelto. */
  icon?: string;
  currentAvg: number;
  pastAvg: number;
}

function fundamentalName(fundamental: FundamentalCatalogItem, lang: string): string {
  return fundamental.shortNames?.[lang] ?? fundamental.shortName
    ?? fundamental.names?.[lang] ?? fundamental.id;
}

/**
 * Arma los ejes del radar en orden de catálogo: el fundamental cuando no tiene
 * comportamientos activos, uno por comportamiento cuando sí los tiene — mismo criterio
 * que ya usa `buildFeedbackItems` para el Acto 03 de esta vista.
 */
export function buildRadarAxes(
  catalogData: readonly FundamentalCatalogItem[],
  fundAvgs: readonly FundAvg[],
  pastFundAvgs: readonly FundAvg[],
  attitudeAvgs: readonly AttitudeAvg[],
  pastAttitudeAvgs: readonly AttitudeAvg[],
  lang: string,
): RadarAxis[] {
  const sortedCatalog = [...catalogData].sort((first, second) => first.order - second.order);
  const axes: RadarAxis[] = [];

  for (const fundamental of sortedCatalog) {
    const activeAttitudes = fundamental.attitudes
      .filter(attitude => attitude.isActive)
      .sort((first, second) => first.order - second.order);

    if (activeAttitudes.length === 0) {
      axes.push({
        id: fundamental.id,
        kind: 'fundamental',
        name: fundamentalName(fundamental, lang),
        icon: fundamental.icon,
        currentAvg: fundAvgs.find(entry => entry.id === fundamental.id)?.avg ?? 0,
        pastAvg: pastFundAvgs.find(entry => entry.id === fundamental.id)?.avg ?? 0,
      });
      continue;
    }

    for (const attitude of activeAttitudes) {
      axes.push({
        id: attitude.id,
        kind: 'attitude',
        name: getAttitudeName(attitude, lang),
        // La imagen del comportamiento manda: nombra la conducta concreta, no la familia.
        icon: attitude.image ?? fundamental.icon,
        currentAvg: attitudeAvgs.find(entry => entry.id === attitude.id)?.avg ?? 0,
        pastAvg: pastAttitudeAvgs.find(entry => entry.id === attitude.id)?.avg ?? 0,
      });
    }
  }

  return axes;
}
